import { InputBase, MenuItem, Paper, Select } from "@mui/material";
import emailjs from "@emailjs/browser";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { HiArrowLongRight } from "react-icons/hi2";

import SlideTransition from "../../components/SlideTransition";

import { GlobalContext } from "../../contexts/GlobalContext";
import { useInView } from "../../hooks/useInView";
import { content } from "../../utils/content";

const INIT_DETAILS = {
  name: "",
  lastName: "",
  email: "",
  phone: "",
  purpose: "",
  message: "",
  website: "IP Management Contact Form",
};

const ContactUsPage = () => {
  const { state, dispatch } = useContext(GlobalContext);

  const [setRef, visible] = useInView({
    threshold: 0.1,
  });
  const { t } = useTranslation();

  const [contactDetails, setContactDetails] = useState(INIT_DETAILS);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);
  const [showRequired, setShowRequired] = useState(false);

  const handleSelectPurpose = (event) => {
    setContactDetails({
      ...contactDetails,
      [event.target.name]: event.target.value,
    });

    setInvalidEmail(false);
    setShowRequired(false);
  };
  const handleChangeInput = (event) => {
    setContactDetails({
      ...contactDetails,
      [event.target.name]: event.target.value,
    });

    setInvalidEmail(false);
    setShowRequired(false);
  };

  const checkIsEmpty = () => {
    const checker =
      contactDetails.name.trim() === "" ||
      contactDetails.lastName.trim() === "" ||
      contactDetails.email.trim() === "" ||
      contactDetails.message.trim() === "" ||
      contactDetails.purpose === ""
        ? true
        : false;
    setShowRequired(checker);

    return checker;
  };

  const validateEmail = (email) => {
    const regex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const validate = regex.test(String(email).toLowerCase());
    setInvalidEmail(!validate);

    return validate;
  };

  const handleSubmitMessage = async (event) => {
    event.preventDefault();

    const isEmpty = checkIsEmpty();
    const validEmail = validateEmail(contactDetails.email.trim());

    if (validEmail && !isEmpty) {
      setIsLoading(true);

      try {
        const response = await emailjs.send(
          process.env.REACT_APP_EMAIL_SERVICE_ID,
          process.env.REACT_APP_EMAIL_TEMPLATE_ID,
          contactDetails,
          process.env.REACT_APP_EMAIL_PUBLIC_KEY
        );

        setContactDetails(INIT_DETAILS);

        dispatch({
          type: "SHOW_SNACKBAR",
          payload: {
            message: t(
              "Thanks for your feedback. We will reply to you shortly."
            ),
            isOpen: true,
          },
        });
      } catch (err) {
        console.error(err);
        if (err) {
          dispatch({
            type: "SHOW_SNACKBAR",
            payload: {
              message: t(
                "Oops... Your message was not sent. Please try again."
              ),
              isOpen: true,
            },
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div
      className="bg-white flex items-start justify-center min-h-[100vh] relative w-full"
      ref={setRef}
    >
      <div className="flex flex-col items-start justify-start max-w-screen-xl px-3 py-4 space-y-8 w-full xl:px-0 xl:py-12 xl:space-y-20">
        <div className="flex flex-col justify-start space-y-2 w-full">
          <SlideTransition appear={visible} timeout={400}>
            <h1 className="capitalize font-bold leading-snug text-4xl  text-secondary tracking-wide sm:leading-normal sm:text-6xl">
              {t(content.contactForm.header)}
            </h1>
          </SlideTransition>

          <SlideTransition appear={visible} timeout={400}>
            <p className="font-normal leading-normal px-1 text-gray-600 text-sm tracking-wide sm:text-base">
              {t(content.contactForm.subheader)}
            </p>
          </SlideTransition>
        </div>

        <SlideTransition appear={visible} className="w-full" timeout={600}>
          <div className="flex flex-col gap-y-3 w-full">
            <Select
              className="!border-gray-300 !text-base"
              displayEmpty
              inputProps={{ "aria-label": "contact purpose" }}
              name="purpose"
              onChange={handleSelectPurpose}
              value={contactDetails?.purpose}
            >
              <MenuItem className="!hidden" disabled value="">
                {t(content.contactForm.optionPlaceholder)}
              </MenuItem>

              {content.contactForm.options.map((option, index) => {
                return (
                  <MenuItem
                    className="!py-3 !text-base"
                    key={index}
                    value={option}
                  >
                    {t(option)}
                  </MenuItem>
                );
              })}
            </Select>

            <div className="flex flex-col sm:space-x-3 space-y-3 w-full sm:flex-row sm:space-y-0">
              <Paper className="!bg-white border !border-gray-300 !duration-300 ease-in-out !flex-1 rounded-sm !shadow-none transition-all hover:!border-secondary">
                <InputBase
                  className="!px-2 !py-2 !text-base !w-full"
                  name="name"
                  onChange={handleChangeInput}
                  placeholder={t("Name*")}
                  value={contactDetails?.name}
                />
              </Paper>

              <Paper className="!bg-white border !border-gray-300 !duration-300 ease-in-out !flex-1 rounded-sm !shadow-none transition-all hover:!border-secondary">
                <InputBase
                  className="!px-2 !py-2 !text-base !w-full"
                  name="lastName"
                  onChange={handleChangeInput}
                  placeholder={t("Last name*")}
                  value={contactDetails?.lastName}
                />
              </Paper>
            </div>

            <div className="flex flex-col sm:space-x-3 space-y-3 w-full sm:flex-row sm:space-y-0">
              <Paper className="!bg-white border !border-gray-300 !duration-300 ease-in-out !flex-1 rounded-sm !shadow-none transition-all hover:!border-secondary">
                <InputBase
                  className="!px-2 !py-2 !text-base !w-full"
                  name="email"
                  onChange={handleChangeInput}
                  placeholder={t("Email address*")}
                  value={contactDetails?.email}
                />
              </Paper>

              <Paper className="!bg-white border !border-gray-300 !duration-300 ease-in-out !flex-1 rounded-sm !shadow-none transition-all hover:!border-secondary">
                <InputBase
                  className="!px-2 !py-2 !text-base !w-full"
                  name="phone"
                  onChange={handleChangeInput}
                  placeholder={t("Phone number*")}
                  value={contactDetails?.phone}
                />
              </Paper>
            </div>

            <div className="flex flex-col sm:space-x-3 space-y-3 w-full sm:flex-row sm:space-y-0">
              <Paper className="!bg-white border !border-gray-300 !duration-300 ease-in-out !flex-1 rounded-sm !shadow-none transition-all hover:!border-secondary">
                <InputBase
                  className="!px-2 !py-2 !text-base !w-full"
                  multiline
                  name="message"
                  onChange={handleChangeInput}
                  placeholder={t("Your message*")}
                  rows={8}
                  value={contactDetails?.message}
                />
              </Paper>
            </div>

            <div className="flex flex-col space-y-1 w-full">
              {showRequired && (
                <p className="text-error text-sm">
                  {t("Please complete all required fields.")}
                </p>
              )}

              {invalidEmail && (
                <p className="text-error text-sm">
                  {t("Please provide a valid email address.")}
                </p>
              )}
            </div>
          </div>
        </SlideTransition>

        <SlideTransition appear={visible} className="w-full" timeout={800}>
          <button
            className="btn-custom btn-primary flex gap-x-2 items-center justify-center w-full sm:w-fit"
            disabled={isLoading}
            onClick={handleSubmitMessage}
            onMouseEnter={() => setMouseOver(true)}
            onMouseLeave={() => setMouseOver(false)}
          >
            {isLoading ? (
              "Sending..."
            ) : (
              <>
                {t(content.contactForm.action)}

                <HiArrowLongRight
                  size={"1.4em"}
                  style={{
                    transform: mouseOver
                      ? "translateX(8px)"
                      : "translateX(2px)",
                    transition: "all 0.3s ease-in-out",
                  }}
                />
              </>
            )}
          </button>
        </SlideTransition>
      </div>
    </div>
  );
};

export default ContactUsPage;
